import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Input, Label, TextField } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import type { CalculatorDirection } from '@/api/models';
import { formatGel } from '@/pages/accounting/books/chartOfAccounts/utils';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    CALCULATOR_DIRECTIONS,
    directionFieldLabelKey,
} from '../../dividendsMeta';
import { useExtractionCalculator } from './useExtractionCalculator';

type ExtractionCalculatorProps = {
    asOf: string;
};

/** A figure in the waterfall, with its role in the story. */
const Step: React.FC<{
    label: string;
    value: string;
    tone?: 'neutral' | 'cost' | 'result';
    hint?: string;
    slot: string;
}> = ({ label, value, tone = 'neutral', hint, slot }) => (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
        <span className="flex min-w-0 flex-col">
            <span
                className={cn(
                    'text-sm',
                    tone === 'result'
                        ? 'font-semibold text-foreground'
                        : 'text-muted',
                )}
            >
                {label}
            </span>
            {hint ? <span className="text-xs text-muted">{hint}</span> : null}
        </span>
        <span
            className={cn(
                'shrink-0 tabular-nums',
                tone === 'result'
                    ? 'text-lg font-bold text-foreground'
                    : tone === 'cost'
                      ? 'text-sm font-medium text-danger'
                      : 'text-sm font-medium text-foreground',
            )}
            {...getNodeTestIdProp(`dividends-calculator-${slot}`)}
        >
            {value}
        </span>
    </div>
);

/**
 * The extraction calculator — the centerpiece of the screen.
 *
 * It works in **all three directions**: from what you are willing to charge
 * against retained earnings, from the gross dividend you want to award, or
 * backwards from what the shareholder should actually receive. Whichever way it
 * is driven, it answers the same question and agrees with itself exactly, because
 * all three call the same arithmetic on the server.
 *
 * The layout is a **waterfall**, not a table, because the point is not the
 * individual figures — it is watching ₾100,000 of retained profit become ₾80,750
 * in an owner's hand. The effective rate is the payoff line, and it is the number
 * this screen exists to make legible enough to decide on.
 *
 * A distribution larger than the entity can currently fund is **priced anyway**,
 * with the shortfall stated. Refusing to answer would make the calculator useless
 * for the question people actually ask, which is "how much *could* I take out?".
 */
export const ExtractionCalculator: React.FC<ExtractionCalculatorProps> = ({
    asOf,
}) => {
    const { t } = useTranslation('pages');
    const {
        direction,
        amount,
        onDirectionChange,
        onAmountChange,
        quote,
        figures,
        invalidAmount,
    } = useExtractionCalculator({ asOf });

    return (
        <section
            aria-label={t('dividends.calculator.title')}
            className="flex flex-col gap-4 rounded-2xl border border-separator bg-surface p-5"
            {...getNodeTestIdProp('dividends-calculator')}
        >
            <div className="flex flex-col gap-1">
                <h2 className="text-sm font-semibold text-foreground">
                    {t('dividends.calculator.title')}
                </h2>
                <p className="text-xs text-muted">
                    {t('dividends.calculator.intro')}
                </p>
            </div>

            <div className="flex flex-wrap items-end gap-4">
                <Segment
                    aria-label={t('dividends.calculator.directionAria')}
                    onSelectionChange={(key) =>
                        onDirectionChange(key as CalculatorDirection)
                    }
                    selectedKey={direction}
                    size="sm"
                    {...getNodeTestIdProp('dividends-calculator-direction')}
                >
                    {CALCULATOR_DIRECTIONS.map((option) => (
                        <Segment.Item
                            id={option.id}
                            key={option.id}
                            {...getNodeTestIdProp(
                                `dividends-calculator-direction-${option.id}`,
                            )}
                        >
                            <Segment.Separator />
                            {t(option.labelKey)}
                        </Segment.Item>
                    ))}
                </Segment>

                <TextField
                    className="flex w-full max-w-[16rem] flex-col gap-1"
                    onChange={onAmountChange}
                    value={amount}
                    {...getNodeTestIdProp('dividends-calculator-amount')}
                >
                    <Label>{t(directionFieldLabelKey(direction))}</Label>
                    <Input
                        className="text-right tabular-nums"
                        inputMode="decimal"
                        placeholder="0.00"
                        variant="secondary"
                    />
                </TextField>
            </div>

            {figures && quote ? (
                <>
                    <div className="flex flex-col divide-y divide-separator/60">
                        <Step
                            label={t('dividends.calculator.charge')}
                            slot="charge"
                            value={figures.chargeText}
                        />
                        <Step
                            hint={
                                figures.isCitFree
                                    ? t('dividends.calculator.noCitHint')
                                    : t('dividends.calculator.citHint')
                            }
                            label={t('dividends.calculator.cit')}
                            slot="cit"
                            tone="cost"
                            value={figures.citText}
                        />
                        <Step
                            label={t('dividends.calculator.grossDividend')}
                            slot="gross"
                            value={figures.grossDividendText}
                        />
                        <Step
                            hint={t('dividends.calculator.withholdingHint')}
                            label={t('dividends.calculator.withholding')}
                            slot="withholding"
                            tone="cost"
                            value={figures.withholdingText}
                        />
                        <Step
                            label={t('dividends.calculator.net')}
                            slot="net"
                            tone="result"
                            value={figures.netText}
                        />
                    </div>

                    <div
                        className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-surface-secondary px-3 py-2"
                        {...getNodeTestIdProp(
                            'dividends-calculator-effective-rate',
                        )}
                    >
                        <span className="text-xs text-muted">
                            {t('dividends.calculator.effectiveRateLabel')}
                        </span>
                        <span className="text-base font-bold tabular-nums text-foreground">
                            {figures.effectiveRateText}
                        </span>
                    </div>

                    {quote.exceedsDistributable ? (
                        <p
                            className="flex items-start gap-2 rounded-xl bg-warning-soft px-3 py-2 text-sm text-warning-soft-foreground"
                            {...getNodeTestIdProp(
                                'dividends-calculator-shortfall',
                            )}
                        >
                            <Icon
                                aria-hidden
                                className="mt-0.5 shrink-0"
                                icon="gravity-ui:circle-info"
                            />
                            {t('dividends.calculator.exceedsDistributable', {
                                shortfall: formatGel(quote.shortfallTetri),
                            })}
                        </p>
                    ) : null}
                </>
            ) : invalidAmount ? (
                <p
                    className="flex items-start gap-2 text-sm text-danger"
                    {...getNodeTestIdProp('dividends-calculator-invalid')}
                >
                    <Icon
                        aria-hidden
                        className="mt-0.5 shrink-0"
                        icon="gravity-ui:circle-xmark"
                    />
                    {t('dividends.calculator.invalidAmount')}
                </p>
            ) : (
                <p className="text-sm text-muted">
                    {t('dividends.calculator.enterAmount')}
                </p>
            )}
        </section>
    );
};
